import { useState, useEffect, useRef } from 'react';

const SEEN_KEY = 'vbot_notif_seen_v1';
const loadSeen = () => {
  try {
    const o = JSON.parse(localStorage.getItem(SEEN_KEY) || '{}');
    const cutoff = Date.now() - 3 * 24 * 3600 * 1000;
    Object.keys(o).forEach(k => { if (o[k] < cutoff) delete o[k]; });
    return o;
  } catch (e) { return {}; }
};
const saveSeen = (o) => { try { localStorage.setItem(SEEN_KEY, JSON.stringify(o)); } catch (e) {} };
const dayKey = (d) => { const x = new Date(d); return isNaN(x) ? '' : x.getFullYear() + '-' + (x.getMonth() + 1) + '-' + x.getDate(); };
const who = (l) => l.name || l.phone || 'Lead';
const subOf = (l) => l.product_tag ? `📦 ${l.product_tag}` : `📞 ${l.source || 'manual'}`;
const CLOSED = ['converted', 'lost'];

export function LeadNotificationBell({ leads }) {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const lastCount = useRef(null);
  const seen = useRef(loadSeen());

  const mk = (type, text, sub, key) => ({
    id: Date.now() + Math.random(), type, text, sub, key: key || null, ts: Date.now(),
    read: key ? !!seen.current[key] : false,
  });
  const push = (items) => setNotifs(prev => {
    const have = new Set(prev.map(n => n.key).filter(Boolean));
    const fresh = items.filter(n => !n.key || !have.has(n.key));
    return fresh.length ? [...fresh, ...prev].slice(0, 30) : prev;
  });
  const markSeen = (keys) => { keys.filter(Boolean).forEach(k => { seen.current[k] = Date.now(); }); saveSeen(seen.current); };

  // New leads (first load is only the baseline, not notified)
  useEffect(() => {
    if (lastCount.current === null) {
      if (leads.length > 0) lastCount.current = leads.length;
      return;
    }
    if (leads.length > lastCount.current) {
      const fresh = leads.slice(0, leads.length - lastCount.current);
      push(fresh.map(l => mk('new_lead', `New lead: ${who(l)}`, subOf(l))));
    }
    lastCount.current = leads.length;
  }, [leads.length]);

  // Follow-up due today, missed follow-ups, response-time (SLA) warnings
  useEffect(() => {
    const today = dayKey(new Date());
    const startToday = new Date(new Date().toDateString());
    const active = leads.filter(l => !CLOSED.includes(l.status));

    const due = active.filter(l => l.follow_up_date && dayKey(l.follow_up_date) === today);
    push(due.map(l => mk('followup_reminder', `⏰ Follow-up due: ${who(l)}`, subOf(l), 'due:' + l.id + ':' + today)));

    const missed = active.filter(l => l.follow_up_date && new Date(l.follow_up_date) < startToday);
    if (missed.length > 0) {
      push([mk('missed_followup', `⚠️ ${missed.length} missed follow-up${missed.length > 1 ? 's' : ''}`, 'Open Daily View to clear them', 'missed:' + today + ':' + missed.length)]);
    }

    const sla = active.filter(l => l.sla_display === 'warning' || l.sla_display === 'exceeded').slice(0, 5);
    push(sla.map(l => mk('sla', l.sla_display === 'exceeded' ? `🚨 Response time exceeded: ${who(l)}` : `⏳ Respond soon: ${who(l)}`, subOf(l), 'sla:' + l.id + ':' + l.sla_display + ':' + today)));
  }, [leads]);

  const unread = notifs.filter(n => !n.read).length;
  const markAll = () => { markSeen(notifs.map(n => n.key)); setNotifs(notifs.map(n => ({ ...n, read: true }))); };
  const clickOne = (n) => { markSeen([n.key]); setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x)); };
  const timeAgo = (ts) => {
    const m = Math.floor((Date.now() - ts) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    return `${Math.floor(m / 60)}h ago`;
  };

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={{
        background: open ? 'rgba(255,255,255,0.2)' : 'transparent',
        border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', position: 'relative', fontSize: 18,
      }}>
        🔔
        {unread > 0 && (
          <span style={{ position: 'absolute', top: 2, right: 2, background: '#f44336', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{ position: 'absolute', top: 44, right: 0, width: 320, background: '#fff', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', zIndex: 2000, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#1a1a2e' }}>🔔 Notifications</div>
            {unread > 0 && <button onClick={markAll} style={{ fontSize: 11, color: '#25d366', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>Mark all read</button>}
          </div>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {notifs.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: '#bbb', fontSize: 12 }}>No notifications yet</div>
            )}
            {notifs.map(n => (
              <div key={n.id} onClick={() => clickOne(n)}
                style={{ padding: '12px 16px', borderBottom: '1px solid #f8f8f8', background: n.read ? '#fff' : '#f0faf5', cursor: 'pointer' }}>
                <div style={{ fontSize: 12, fontWeight: n.read ? 500 : 700, color: '#1a1a2e' }}>{n.text}</div>
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{n.sub} · {timeAgo(n.ts)}</div>
              </div>
            ))}
          </div>
          <div style={{ padding: '10px 16px', borderTop: '1px solid #f0f0f0', fontSize: 11, color: '#bbb', textAlign: 'center' }}>
            New leads, follow-ups and response-time alerts appear here
          </div>
        </div>
      )}
    </div>
  );
}
